import { useStore } from '../core/store.ts';
import { useStrings } from '../i18n/index.ts';
import { paymentConfigStore, savedCardsStore, type PaymentMethod } from '../state/payment.ts';
import { BankBadge } from './BankBadge.tsx';
import { brandOf } from './CardForm.tsx';
import { Icon } from './icons.tsx';
import { cx } from './kit.tsx';

/**
 * Плитки способа оплаты: сохранённые карты (бейдж банка и последние цифры), «новая карта»,
 * страница банка, тестовая карта мока. Без сохранённых карт и тестовой карты ряд не показывается:
 * форма карты открывается по «Оплатить». Нажатие на «новую карту» сразу открывает форму (это
 * делает экран по выбору `new`); `newCardDigits` — цифры уже введённой карты: плитка показывает
 * её систему и последние четыре.
 */
export function PaymentPicker(props: { method: PaymentMethod; onChange: (method: PaymentMethod) => void; newCardDigits?: string | undefined }) {
  const strings = useStrings();
  const config = useStore(paymentConfigStore);
  const cards = useStore(savedCardsStore).items;
  const inline = config.cardEntry === 'inline';
  // Плитка тестовой карты только при вводе на странице банка: с формой в приложении тест идёт через неё.
  const testOffered = config.testCard && !inline;
  const showsRow = (inline && cards.length > 0) || testOffered;
  if (!showsRow) return null;

  const selected = (kind: PaymentMethod['kind'], cardId?: string) => props.method.kind === kind && (kind !== 'saved' || (props.method.kind === 'saved' && props.method.cardId === cardId));

  return (
    <div className="payment-tiles" role="radiogroup" aria-label={strings.payWith}>
      {inline &&
        cards.map((card) => (
          <button
            key={card.id}
            type="button"
            className={cx('payment-tile', selected('saved', card.id) && 'is-selected')}
            role="radio"
            aria-checked={selected('saved', card.id)}
            onClick={() => props.onChange({ kind: 'saved', cardId: card.id })}
          >
            <BankBadge issuer={card.issuer} brand={card.brand} />
            <span className="payment-tile-digits t-numeric">·· {card.last4 ?? '????'}</span>
          </button>
        ))}
      {inline && (
        <button type="button" className={cx('payment-tile is-new', selected('new') && 'is-selected')} role="radio" aria-checked={selected('new')} onClick={() => props.onChange({ kind: 'new' })}>
          {props.newCardDigits ? (
            <>
              <BankBadge issuer={null} brand={brandOf(props.newCardDigits)} />
              <span className="payment-tile-digits t-numeric">·· {props.newCardDigits.slice(-4)}</span>
            </>
          ) : (
            <>
              <Icon name="plus" size={18} weight={2.4} />
              <span>{strings.newCard}</span>
            </>
          )}
        </button>
      )}
      {!inline && config.hosted && (
        <button type="button" className={cx('payment-tile is-wide', selected('bank') && 'is-selected')} role="radio" aria-checked={selected('bank')} onClick={() => props.onChange({ kind: 'bank' })}>
          <Icon name="card" size={20} />
          <span>{strings.paymentCardTitle}</span>
        </button>
      )}
      {testOffered && (
        <button type="button" className={cx('payment-tile is-wide', selected('test') && 'is-selected')} role="radio" aria-checked={selected('test')} onClick={() => props.onChange({ kind: 'test' })}>
          <Icon name="card" size={20} />
          <span>{strings.testCard}</span>
        </button>
      )}
    </div>
  );
}
